import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import type { CSVData } from "../../EngagementInterfaces";

dayjs.extend(customParseFormat);

export const LAST_ORDER_FORMAT = "DD MMM YYYY";

export type ListField =
  | "firstName"
  | "lastName"
  | "fullName"
  | "email"
  | "phone"
  | "totalOrders"
  | "totalSpent"
  | "lastOrder";

export type ListColumnMap = Partial<Record<ListField, string>>;

export interface ListRow {
  id: number;
  data: CSVData;
}

export interface AddRowValues {
  name?: string;
  email?: string;
  phone?: string;
  totalOrders?: string;
  totalSpent?: string;
  lastOrder?: string;
}

const FIELD_ALIASES: Record<ListField, string[]> = {
  firstName: ["firstname", "fname", "first"],
  lastName: ["lastname", "lname", "last", "surname"],
  fullName: ["name", "fullname", "customername", "customer"],
  email: ["email", "emailaddress", "emailid", "mail"],
  phone: ["phone", "phonenumber", "phoneno", "mobile", "mobilenumber", "contactnumber"],
  totalOrders: ["totalorders", "orders", "ordercount", "orderscount", "numberoforders"],
  totalSpent: ["totalspent", "totalspend", "spent", "amountspent"],
  lastOrder: ["lastorder", "lastorderdate", "lastorderat", "lastpurchase", "lastpurchasedate"],
};

const normalizeHeader = (header: string) => header.toLowerCase().replace(/[^a-z0-9]/g, "");

export const detectListColumns = (headers: string[]): ListColumnMap =>
  (Object.keys(FIELD_ALIASES) as ListField[]).reduce<ListColumnMap>((columns, field) => {
    const header = headers.find(item => FIELD_ALIASES[field].includes(normalizeHeader(item)));
    if (header) {
      columns[field] = header;
    }
    return columns;
  }, {});

export const hasNameColumn = (columns: ListColumnMap) =>
  Boolean(columns.fullName || columns.firstName || columns.lastName);

export const readField = (row: CSVData, columns: ListColumnMap, field: ListField): string => {
  const header = columns[field];
  return header ? (row[header] ?? "").trim() : "";
};

export const getCustomerName = (row: CSVData, columns: ListColumnMap): string =>
  readField(row, columns, "fullName") ||
  [readField(row, columns, "firstName"), readField(row, columns, "lastName")]
    .filter(Boolean)
    .join(" ");

export const formatLastOrder = (value: string): string => {
  if (!value) {
    return "";
  }
  const strict = dayjs(value, LAST_ORDER_FORMAT, true);
  const parsed = strict.isValid() ? strict : dayjs(value);
  return parsed.isValid() ? parsed.format(LAST_ORDER_FORMAT) : value;
};

export const rowMatchesQuery = (row: CSVData, columns: ListColumnMap, query: string) =>
  [getCustomerName(row, columns), readField(row, columns, "email"), readField(row, columns, "phone")]
    .some(value => value.toLowerCase().includes(query));

const splitName = (name: string) => {
  const [firstName = "", ...rest] = name.trim().split(/\s+/);
  return { firstName, lastName: rest.join(" ") };
};

export const buildRowFromValues = (
  headers: string[],
  columns: ListColumnMap,
  values: AddRowValues,
): CSVData => {
  const row = headers.reduce<CSVData>((acc, header) => {
    acc[header] = "";
    return acc;
  }, {});
  const assign = (field: ListField, value: string | undefined) => {
    const header = columns[field];
    if (header) {
      row[header] = value?.trim() ?? "";
    }
  };

  const name = values.name?.trim() ?? "";
  if (columns.fullName) {
    assign("fullName", name);
  } else {
    const { firstName, lastName } = splitName(name);
    assign("firstName", columns.lastName ? firstName : name);
    assign("lastName", lastName);
  }
  assign("email", values.email);
  assign("phone", values.phone);
  assign("totalOrders", values.totalOrders);
  assign("totalSpent", values.totalSpent);
  assign("lastOrder", values.lastOrder);
  return row;
};
